import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Eye, EyeOff } from "lucide-react";

function formatError(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async e => {
    e.preventDefault();
    if (form.password.length < 6) { setError("Password must be at least 6 characters."); return; }
    setLoading(true);
    setError("");
    try {
      await register(form.name, form.email, form.password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ivory flex flex-col items-center justify-center px-6 py-20" data-testid="register-page">
      <div className="w-full max-w-sm">
        <div className="text-center mb-10">
          <Link to="/">
            <img src="https://customer-assets.emergentagent.com/job_beyond-stuck/artifacts/4bqnieq1_Company%20Logo.jpeg" alt="Logo" className="h-14 w-14 mx-auto mb-4 rounded-sm" />
          </Link>
          <p className="font-lora text-xs tracking-[0.3em] uppercase text-gold mb-2">De Nicola Consulting Co.</p>
          <h1 className="font-lora text-2xl text-navy font-normal">Create Your Account</h1>
          <div className="w-8 h-px bg-gold mx-auto mt-4"></div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block font-lora text-xs tracking-[0.15em] uppercase text-navy/60 mb-2">Full Name</label>
            <input
              type="text"
              value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              required
              className="w-full border border-soft-gray bg-white px-4 py-3 text-navy font-serif text-sm focus:outline-none focus:border-navy"
              placeholder="Your full name"
              data-testid="register-name-input"
            />
          </div>
          <div>
            <label className="block font-lora text-xs tracking-[0.15em] uppercase text-navy/60 mb-2">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
              required
              className="w-full border border-soft-gray bg-white px-4 py-3 text-navy font-serif text-sm focus:outline-none focus:border-navy"
              placeholder="your@email.com"
              data-testid="register-email-input"
            />
          </div>
          <div className="relative">
            <label className="block font-lora text-xs tracking-[0.15em] uppercase text-navy/60 mb-2">Password</label>
            <input
              type={showPwd ? "text" : "password"}
              value={form.password}
              onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
              required
              className="w-full border border-soft-gray bg-white px-4 py-3 pr-12 text-navy font-serif text-sm focus:outline-none focus:border-navy"
              placeholder="Min. 6 characters"
              data-testid="register-password-input"
            />
            <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-[38px] text-navy/40 hover:text-navy">
              {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>

          {error && <p className="text-red-600 font-serif text-sm" data-testid="register-error">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-navy text-ivory py-3.5 text-sm tracking-[0.15em] uppercase hover:bg-navy/90 transition-all flex items-center justify-center gap-2 disabled:opacity-60 mt-2"
            data-testid="register-submit-btn"
          >
            {loading ? <><Loader2 size={15} className="animate-spin" /> Creating Account...</> : "Create Account"}
          </button>
        </form>

        <p className="text-center text-navy/50 font-serif text-sm mt-6">
          Already have an account?{" "}
          <Link to="/login" className="text-navy hover:text-gold transition-colors underline" data-testid="register-login-link">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
